import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { Shield, Lock, Eye, Database } from 'lucide-react';
export function Security() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const badges = [{
    label: 'GDPR',
    description: 'Cumplimiento total'
  }, {
    label: 'PCI DSS',
    description: 'Cumplimiento'
  }, {
    label: 'ISO 27001',
    description: 'Seguridad certificada'
  }, {
    label: 'SOC 2',
    description: 'Tipo II'
  }];
  const metrics = [{
    value: '+2M',
    label: 'Identidades verificadas'
  }, {
    value: '99.99%',
    label: 'Uptime garantizado'
  }, {
    value: '<3s',
    label: 'Tiempo de verificación'
  }];
  return <section id="seguridad" className="py-20 md:py-32" ref={ref} data-id="element-290">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-291">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-292">
          {/* Left Column - Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: -30
        }} transition={{
          duration: 0.6
        }} data-id="element-293">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-white mb-6" data-id="element-294">
              Seguridad sin compromisos
            </h2>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-295">
              Nuestra arquitectura de seguridad está diseñada desde cero para
              proteger tu privacidad. Utilizamos pruebas de conocimiento cero,
              cifrado de extremo a extremo y minimización de datos para
              garantizar que tus datos personales permanezcan siempre bajo tu
              control.
            </p>

            <div className="space-y-4" data-id="element-296">
              <div className="flex items-start space-x-3" data-id="element-297">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-298">
                  <Shield size={20} className="text-teal-500" data-id="element-299" />
                </div>
                <div data-id="element-300">
                  <h4 className="font-semibold text-white mb-1" data-id="element-301">
                    Zero-Knowledge Proofs
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-302">
                    Verifica sin revelar información sensible
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-303">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-304">
                  <Lock size={20} className="text-teal-500" data-id="element-305" />
                </div>
                <div data-id="element-306">
                  <h4 className="font-semibold text-white mb-1" data-id="element-307">Cifrado E2E</h4>
                  <p className="text-sm text-gray-400" data-id="element-308">
                    Tus datos están protegidos en todo momento
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-309">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-310">
                  <Eye size={20} className="text-teal-500" data-id="element-311" />
                </div>
                <div data-id="element-312">
                  <h4 className="font-semibold text-white mb-1" data-id="element-313">
                    Transparencia Total
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-314">Auditorías regulares</p>
                </div>
              </div>

              <div className="flex items-start space-x-3" data-id="element-315">
                <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-316">
                  <Database size={20} className="text-teal-500" data-id="element-317" />
                </div>
                <div data-id="element-318">
                  <h4 className="font-semibold text-white mb-1" data-id="element-319">
                    Minimización de Datos
                  </h4>
                  <p className="text-sm text-gray-400" data-id="element-320">
                    Solo recopilamos lo estrictamente necesario
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column - Badges & Metrics */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {
          opacity: 0,
          x: 30
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="space-y-8" data-id="element-321">
            {/* Compliance Badges */}
            <div data-id="element-322">
              <h3 className="text-xl font-display font-semibold text-white mb-4" data-id="element-323">
                Certificaciones y cumplimiento
              </h3>
              <div className="grid grid-cols-2 gap-4" data-id="element-324">
                {badges.map((badge, index) => <motion.div key={badge.label} initial={{
                opacity: 0,
                scale: 0.9
              }} animate={isInView ? {
                opacity: 1,
                scale: 1
              } : {
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.4,
                delay: 0.3 + index * 0.1
              }} className="bg-navy-800 border border-teal-500/20 rounded-lg p-4 text-center hover:border-teal-500/40 transition-colors" data-id="element-325">
                    <div className="text-2xl font-display font-bold text-teal-500 mb-1" data-id="element-326">
                      {badge.label}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-327">
                      {badge.description}
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}